import { useEffect, useRef, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PropertyPhotoRender — Desire Studio automated concept renderer.
 *
 * Feb 11, 2026 (Shannon directive):
 *   • Fully automated — no manual system variable entry.
 *   • Unit configuration (studio → 4BR/2BA, includes 2BR/1BA) drives
 *     material quantities server-side. LP just picks the config + palette.
 *   • Flagship palette: BLACK EXCELLENCE (walnut + brass + emerald + cream).
 *   • LP-facing side-by-side financial tracker:
 *       Phase 1 (Mobilization) — user procurement cost = $0
 *       Phase 2 (Stabilization) — second half baked into service fee (rent × 1.20)
 *   • Vendor logos + SKUs stripped from LP view; crew Materials Manifest
 *     retains full detail via role-gating.
 */
const PropertyPhotoRender = ({ open, onClose }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [palette, setPalette] = useState("black_excellence");
  const [unitConfig, setUnitConfig] = useState("2br_1ba");
  const [slot, setSlot] = useState("kitchen");
  const [marketRent, setMarketRent] = useState(1250);
  const [rendering, setRendering] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const [palettes, setPalettes] = useState([]);
  const [unitConfigs, setUnitConfigs] = useState([]);
  const [feeSnapshot, setFeeSnapshot] = useState(null);
  const [feeLoading, setFeeLoading] = useState(false);
  const [publicLines, setPublicLines] = useState([]);
  // Generative variant state — used when palette = "black_excellence"
  const [variantSeed, setVariantSeed] = useState(() => `unit-${Date.now().toString(36)}`);
  const [rotation, setRotation] = useState(0);
  const [variant, setVariant] = useState(null);

  // Load palette metadata + unit configs on open
  useEffect(() => {
    if (!open) return;
    fetch(`${BACKEND_URL}/api/design/palettes`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : { palettes: [] })
      .then((d) => setPalettes(d.palettes || []))
      .catch(() => setPalettes([]));
    fetch(`${BACKEND_URL}/api/design/unit-configs`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : { unit_configs: [] })
      .then((d) => setUnitConfigs(d.unit_configs || []))
      .catch(() => setUnitConfigs([]));
  }, [open]);

  // Auto-recompute fee snapshot whenever config or palette or rent changes.
  // For 'black_excellence' → call the GENERATIVE variant endpoint so each
  // unit lands on a unique jewel-tone theme. For other palettes → the
  // static auto-compute endpoint.
  useEffect(() => {
    if (!open) return;
    setFeeLoading(true);
    if (palette === "black_excellence") {
      fetch(`${BACKEND_URL}/api/design/generate-variant`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          seed: variantSeed,
          rotation_offset: rotation,
          unit_config: unitConfig,
          market_rent: Number(marketRent) || 0,
          public_view: true,
        }),
      })
        .then((r) => r.ok ? r.json() : null)
        .then((d) => {
          if (!d) return;
          setFeeSnapshot(d.fee || null);
          setVariant(d.variant || null);
          setPublicLines(d.lines || []);
        })
        .catch(() => { setFeeSnapshot(null); setVariant(null); })
        .finally(() => setFeeLoading(false));
    } else {
      setVariant(null);
      fetch(`${BACKEND_URL}/api/design/auto-compute`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          unit_config: unitConfig,
          palette_id: palette,
          market_rent: Number(marketRent) || 0,
        }),
      })
        .then((r) => r.ok ? r.json() : null)
        .then((d) => setFeeSnapshot(d))
        .catch(() => setFeeSnapshot(null))
        .finally(() => setFeeLoading(false));

      fetch(`${BACKEND_URL}/api/design/palette-lines/${palette}?public_view=true`, { credentials: "include" })
        .then((r) => r.ok ? r.json() : { lines: [] })
        .then((d) => setPublicLines(d.lines || []))
        .catch(() => setPublicLines([]));
    }
  }, [open, unitConfig, palette, marketRent, variantSeed, rotation]);

  if (!open) return null;

  const pickFile = (f) => {
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { setErr("Photo too large (max 15 MB)."); return; }
    setFile(f);
    setErr("");
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) pickFile(f);
  };

  const submit = async () => {
    if (!file) { setErr("Pick a photo first."); return; }
    setRendering(true); setErr(""); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("palette_id", palette);
      fd.append("slot", slot);
      const r = await fetch(`${BACKEND_URL}/api/design/render-from-photo`, {
        method: "POST", credentials: "include", body: fd,
      });
      const d = await r.json();
      if (r.ok) setResult(d.render);
      else setErr(d.detail || `HTTP ${r.status}`);
    } catch { setErr("Network error rendering. Try again."); }
    finally { setRendering(false); }
  };

  const fmt = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const activePalette = palettes.find((p) => p.palette_id === palette);
  // If a generative variant is active, its hex codes override the palette meta.
  const brass = variant?.brass_hex || activePalette?.brass_color || "#B8915A";
  const accent = variant?.accent_hex || activePalette?.accent_color || "#0d5c3a";
  const isGenerative = palette === "black_excellence";
  const rollNewVariant = () => {
    setVariantSeed(`unit-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`);
    setRotation(0);
  };
  const nudgeVariant = () => setRotation((r) => r + 1);

  return (
    <div onClick={onClose}
         style={{ position: "fixed", inset: 0, background: "rgba(2,8,20,0.86)",
                  display: "flex", justifyContent: "center", alignItems: "flex-start",
                  padding: "1.5rem 1rem", zIndex: 9998, overflowY: "auto" }}
         data-testid="photo-render-backdrop">
      <div onClick={(e) => e.stopPropagation()}
           style={{ background: "linear-gradient(160deg, #0a1929 0%, #1b2a4e 100%)",
                    border: `1px solid ${brass}88`, borderRadius: 12,
                    maxWidth: 1080, width: "100%", padding: "1.4rem 1.6rem",
                    color: "#F5EFE1", boxShadow: `0 40px 90px rgba(0,0,0,0.55), 0 0 40px ${brass}22` }}
           data-testid="photo-render-modal">

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem", borderBottom: `1px solid ${brass}44`, paddingBottom: "0.9rem" }}>
          <div>
            <div style={{ color: brass, fontSize: "0.68rem", letterSpacing: "0.24em", textTransform: "uppercase", fontWeight: 800 }}>
              Design Studio · Digital Platform Concept Engine
            </div>
            <h2 style={{ margin: "0.35rem 0 0", fontSize: "1.45rem", fontWeight: 700, letterSpacing: "-0.01em" }}>
              Their Property. Their New Chapter.
            </h2>
            <div style={{ color: "#ADBED1", fontSize: "0.83rem", marginTop: 4, maxWidth: 700 }}>
              Pick the unit configuration and the palette. The system auto-selects modular finishes and locks
              the 50/50 disbursement math — no manual variable entry. The rendered concept becomes the actual
              installed spec. What they see is what they get.
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="photo-render-close"
                  style={{ background: "transparent", color: brass, border: `1px solid ${brass}`,
                           padding: "0.4rem 0.9rem", borderRadius: 4, fontSize: "0.78rem", fontWeight: 700, cursor: "pointer" }}>
            Close ✕
          </button>
        </div>

        {/* Configuration grid — Unit / Palette / Rent / Slot */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "0.7rem", marginBottom: "1rem" }}>
          <div>
            <div style={{ color: brass, fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 4 }}>Unit Config</div>
            <select data-testid="photo-render-unit-config" value={unitConfig} onChange={(e) => setUnitConfig(e.target.value)}
                    style={selStyle(brass)}>
              {unitConfigs.map((u) => (
                <option key={u.key} value={u.key}>{u.label}</option>
              ))}
            </select>
          </div>
          <div>
            <div style={{ color: brass, fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 4 }}>Variation</div>
            <select data-testid="photo-render-palette" value={palette} onChange={(e) => setPalette(e.target.value)}
                    style={selStyle(brass)}>
              {palettes.map((p) => (
                <option key={p.palette_id} value={p.palette_id}>
                  {p.flagship ? "⭐ " : ""}{p.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div style={{ color: brass, fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 4 }}>Market Rent</div>
            <input type="number" data-testid="photo-render-market-rent" value={marketRent}
                   onChange={(e) => setMarketRent(Math.max(0, Number(e.target.value) || 0))}
                   min="0" max="10000" step="25"
                   style={selStyle(brass)} />
          </div>
          <div>
            <div style={{ color: brass, fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 4 }}>Slot</div>
            <select data-testid="photo-render-slot" value={slot} onChange={(e) => setSlot(e.target.value)}
                    style={selStyle(brass)}>
              <option value="kitchen">Kitchen</option>
              <option value="bath">Bathroom</option>
              <option value="living">Living Room</option>
              <option value="bedroom">Bedroom</option>
              <option value="exterior">Exterior / Front Door</option>
            </select>
          </div>
        </div>

        {/* Palette description strip / Generative variant narrative */}
        {isGenerative && variant ? (
          <div data-testid="variant-narrative" style={{
            background: `linear-gradient(90deg, ${accent}30, ${brass}18)`,
            border: `1px solid ${brass}66`, borderRadius: 8,
            padding: "0.75rem 1rem", marginBottom: "1rem",
            display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
            flexWrap: "wrap"
          }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ color: brass, fontSize: "0.66rem", letterSpacing: "0.22em", fontWeight: 800, textTransform: "uppercase", marginBottom: 4 }}>
                Generative Variant · <span data-testid="variant-theme-key">{variant.theme_key.replace(/_/g, " ")}</span>
              </div>
              <div style={{ color: "#F5EFE1", fontSize: "0.88rem", lineHeight: 1.5 }} data-testid="variant-narrative-text">
                {variant.narrative}
              </div>
              <div style={{ color: "#9CA3AF", fontSize: "0.7rem", marginTop: 4 }}>
                No two units share this variant. Cost-efficient modular procurement · luxury result.
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
              <button type="button" onClick={nudgeVariant} data-testid="variant-nudge-btn"
                      style={{ background: "transparent", color: brass, border: `1px solid ${brass}88`,
                               padding: "0.5rem 0.85rem", borderRadius: 5, fontSize: "0.75rem",
                               fontWeight: 700, cursor: "pointer", letterSpacing: "0.03em" }}>
                Nudge →
              </button>
              <button type="button" onClick={rollNewVariant} data-testid="variant-roll-btn"
                      style={{ background: `linear-gradient(90deg, ${brass}, ${accent})`,
                               color: "#0a1929", border: "none", padding: "0.5rem 0.95rem",
                               borderRadius: 5, fontSize: "0.75rem", fontWeight: 800,
                               cursor: "pointer", letterSpacing: "0.04em" }}>
                🎲 New Variant
              </button>
            </div>
          </div>
        ) : activePalette && (
          <div data-testid="palette-description" style={{
            background: `linear-gradient(90deg, ${accent}22, ${brass}12)`,
            border: `1px solid ${brass}55`, borderRadius: 8,
            padding: "0.7rem 0.95rem", marginBottom: "1rem",
            fontSize: "0.85rem", color: "#F5EFE1", lineHeight: 1.5
          }}>
            <strong style={{ color: brass }}>{activePalette.label}</strong>
            <span style={{ color: "#ADBED1", marginLeft: 8 }}>· {activePalette.description}</span>
          </div>
        )}

        {/* Two-column layout: photo dropzone (left) + side-by-side financial tracker (right) */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
          {/* LEFT — photo dropzone */}
          <div
            data-testid="photo-render-dropzone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            onClick={() => fileRef.current?.click()}
            style={{ border: `2px dashed ${brass}88`, borderRadius: 10,
                     padding: preview ? "0.6rem" : "2rem 1rem",
                     textAlign: "center", cursor: "pointer",
                     background: "rgba(0,0,0,0.26)", minHeight: 240,
                     display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}
          >
            {preview ? (
              <img src={preview} alt="Before"
                   style={{ maxHeight: 220, maxWidth: "100%", borderRadius: 6 }} />
            ) : (
              <div>
                <div style={{ fontSize: "2rem" }}>📸</div>
                <div style={{ color: "#F5EFE1", fontSize: "0.92rem", fontWeight: 700, marginTop: 8 }}>
                  Drop the actual property photo here
                </div>
                <div style={{ color: "#9CA3AF", fontSize: "0.75rem", marginTop: 6 }}>
                  What you see rendered is what gets installed. No stage-and-swap.
                </div>
                <div style={{ color: "#9CA3AF", fontSize: "0.72rem", marginTop: 4 }}>
                  JPG or PNG · max 15 MB
                </div>
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/*"
                   style={{ display: "none" }}
                   onChange={(e) => pickFile(e.target.files?.[0])}
                   data-testid="photo-render-file" />
          </div>

          {/* RIGHT — 50/50 financial tracker */}
          <div data-testid="financial-tracker" style={{
            background: "rgba(0,0,0,0.32)",
            border: `1px solid ${brass}55`, borderRadius: 10,
            padding: "0.9rem 1rem", display: "flex", flexDirection: "column", gap: "0.6rem"
          }}>
            <div style={{ color: brass, fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase" }}>
              50 / 50 Disbursement · Auto-Locked
            </div>

            {/* Phase 1 */}
            <div data-testid="phase-1-mobilization" style={{
              background: "rgba(0,220,120,0.08)", border: "1px solid rgba(0,220,120,0.4)",
              borderRadius: 8, padding: "0.6rem 0.75rem"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4, gap: 8, flexWrap: "wrap" }}>
                <div style={{ color: "#00DC78", fontWeight: 800, fontSize: "0.82rem", letterSpacing: "0.08em", flex: "1 1 auto", minWidth: 0 }}>
                  PHASE 1 · MOBILIZATION
                </div>
                <div style={{ color: "#00DC78", fontWeight: 800, fontSize: "1.05rem", whiteSpace: "nowrap", flex: "0 0 auto" }} data-testid="phase-1-user-cost">
                  $0
                </div>
              </div>
              <div style={{ color: "#ADBED1", fontSize: "0.74rem", lineHeight: 1.45 }}>
                Materials procurement is covered by Activation Capital already deployed. LP procurement cost at kickoff: <strong style={{ color: "#00DC78" }}>zero</strong>. Crew mobilizes on Day 1.
              </div>
            </div>

            {/* Phase 2 */}
            <div data-testid="phase-2-stabilization" style={{
              background: `${brass}18`, border: `1px solid ${brass}66`,
              borderRadius: 8, padding: "0.6rem 0.75rem"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4, gap: 8, flexWrap: "wrap" }}>
                <div style={{ color: brass, fontWeight: 800, fontSize: "0.82rem", letterSpacing: "0.08em", flex: "1 1 auto", minWidth: 0 }}>
                  PHASE 2 · STABILIZATION
                </div>
                <div style={{ color: brass, fontWeight: 800, fontSize: "1.05rem", whiteSpace: "nowrap", flex: "0 0 auto" }} data-testid="phase-2-service-fee">
                  {feeLoading ? "…" : fmt(feeSnapshot?.service_fee_monthly)} <span style={{ fontSize: "0.7rem", opacity: 0.75 }}>/ mo</span>
                </div>
              </div>
              <div style={{ color: "#ADBED1", fontSize: "0.74rem", lineHeight: 1.45 }}>
                Second-half mobilization is built into the Service Fee at <strong>(Market Rent × 3) + 20%</strong>. Released monthly on PM sign-off after stabilization.
              </div>
            </div>

            {/* ROI Snapshot */}
            <div data-testid="roi-snapshot" style={{
              background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 8, padding: "0.6rem 0.75rem"
            }}>
              <div style={{ color: "#F5EFE1", fontSize: "0.72rem", letterSpacing: "0.14em", fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>
                Stabilized ROI Signal
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: "0.8rem" }}>
                <div>
                  <div style={{ color: "#9CA3AF", fontSize: "0.7rem" }}>Market Rent</div>
                  <div style={{ color: "#F5EFE1", fontWeight: 700 }} data-testid="market-rent-echo">{fmt(feeSnapshot?.market_rent ?? marketRent)}</div>
                </div>
                <div>
                  <div style={{ color: "#9CA3AF", fontSize: "0.7rem" }}>Stabilized Target</div>
                  <div style={{ color: "#00DC78", fontWeight: 700 }} data-testid="stabilized-target">{fmt(feeSnapshot?.stabilized_rent_target)}</div>
                </div>
                <div>
                  <div style={{ color: "#9CA3AF", fontSize: "0.7rem" }}>Annual Service Fee</div>
                  <div style={{ color: brass, fontWeight: 700 }} data-testid="annual-fee">{fmt(feeSnapshot?.service_fee_annual)}</div>
                </div>
                <div>
                  <div style={{ color: "#9CA3AF", fontSize: "0.7rem" }}>Qual. Min Income</div>
                  <div style={{ color: "#F5EFE1", fontWeight: 700 }} data-testid="qual-income">{fmt(feeSnapshot?.qualifying_min_income_annual)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Systems auto-selected (LP-facing, no vendor names) */}
        {publicLines.length > 0 && (
          <div data-testid="auto-selected-systems" style={{
            background: "rgba(0,0,0,0.24)", border: `1px solid ${accent}66`, borderRadius: 8,
            padding: "0.7rem 0.9rem", marginBottom: "1rem"
          }}>
            <div style={{ color: accent, fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 6 }}>
              Modular Systems Auto-Selected for This Variation
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 6 }}>
              {publicLines.map((l, i) => (
                <div key={`${l.role}-${i}`} data-testid={`public-line-${l.role}`}
                     style={{ background: "rgba(255,255,255,0.03)", borderRadius: 5, padding: "0.4rem 0.55rem", fontSize: "0.78rem", color: "#F5EFE1" }}>
                  <div style={{ color: "#9CA3AF", fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.08em" }}>{l.role_label}</div>
                  <div style={{ fontWeight: 600, marginTop: 2 }}>{l.system_label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {err && (
          <div data-testid="photo-render-error" style={{ background: "rgba(220,38,38,0.14)", border: "1px solid rgba(220,38,38,0.4)", color: "#FCA5A5", padding: "0.5rem 0.8rem", borderRadius: 5, fontSize: "0.82rem", marginBottom: "0.7rem" }}>
            ⚠ {err}
          </div>
        )}

        {!result && (
          <button type="button" onClick={submit} disabled={rendering || !file}
                  data-testid="photo-render-submit"
                  style={{ background: rendering ? `${brass}44` : `linear-gradient(90deg, ${brass}, ${accent})`,
                           color: "#0a1929", border: "none", padding: "0.75rem 1.5rem", borderRadius: 6,
                           fontSize: "0.95rem", fontWeight: 800, width: "100%", letterSpacing: "0.04em",
                           cursor: rendering ? "not-allowed" : "pointer", opacity: rendering || !file ? 0.6 : 1 }}>
            {rendering ? "🎨 Rendering concept… (~15-30 sec)" : "🎨 Render Concept — Lock 50/50 Math"}
          </button>
        )}

        {result && (
          <div data-testid="photo-render-result" style={{ marginTop: "0.9rem" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem", marginBottom: "0.7rem" }}>
              <div>
                <div style={{ color: brass, fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 4 }}>Before (their photo)</div>
                <img src={`${BACKEND_URL}${result.before_url}`} alt="Before"
                     style={{ width: "100%", borderRadius: 6, border: "1px solid rgba(255,255,255,0.1)" }} />
              </div>
              <div>
                <div style={{ color: "#00DC78", fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 4 }}>After · this is what installs</div>
                <img src={`${BACKEND_URL}${result.after_url}`} alt="After"
                     style={{ width: "100%", borderRadius: 6, border: "1px solid rgba(0,220,120,0.5)" }} />
              </div>
            </div>

            <button type="button" onClick={() => { setResult(null); setFile(null); setPreview(null); }}
                    data-testid="photo-render-new"
                    style={{ marginTop: "0.7rem", background: "transparent", color: brass,
                             border: `1px solid ${brass}`, padding: "0.55rem 1rem", borderRadius: 5,
                             fontSize: "0.82rem", fontWeight: 700, cursor: "pointer", width: "100%" }}>
              📸 Render another slot
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const selStyle = (brass) => ({
  width: "100%", background: "rgba(0,0,0,0.32)", color: "#F5EFE1",
  border: `1px solid ${brass}55`, borderRadius: 5,
  padding: "0.55rem 0.7rem", fontSize: "0.85rem", fontWeight: 500
});

export default PropertyPhotoRender;
